// Accessible-tooltips service — when enabled, any element with a
// `title` or `aria-label` attribute shows a visible tooltip on hover
// / focus. Uses a single floating DOM element positioned with JS so
// it doesn't conflict with existing CSS ::after usage (Font Awesome,
// other utility classes) and works on inline elements.

let started = false;
let tooltipEl = null;
let currentTarget = null;
let showTimer = null;
let originalTitles = new WeakMap();

function ensureTooltip() {
  if (tooltipEl) return;
  tooltipEl = document.createElement('div');
  tooltipEl.className = 'a11y-tooltip';
  tooltipEl.setAttribute('role', 'tooltip');
  tooltipEl.style.cssText = [
    'position:fixed',
    'z-index:9999',
    'background:#111827',
    'color:#ffffff',
    'font-family:Poppins,sans-serif',
    'font-size:12px',
    'font-weight:600',
    'line-height:1.4',
    'padding:6px 10px',
    'border-radius:6px',
    'pointer-events:none',
    'max-width:280px',
    'word-wrap:break-word',
    'opacity:0',
    'transition:opacity 120ms',
    'box-shadow:0 4px 12px rgba(0,0,0,0.25)'
  ].join(';');
  document.body.appendChild(tooltipEl);
}

function getTooltipText(el) {
  if (!el || !el.getAttribute) return '';
  // Prefer the real title (preserved in our WeakMap if we stripped it)
  const stored = originalTitles.get(el);
  if (stored) return stored;
  const title = el.getAttribute('title');
  if (title && title.trim()) return title.trim();
  // Fall back to aria-label — but only if the element doesn't have
  // visible text content (otherwise the aria-label duplicates the
  // label the user already sees).
  const aria = el.getAttribute('aria-label');
  if (aria && aria.trim()) {
    const visibleText = (el.innerText || el.textContent || '').trim();
    if (!visibleText || visibleText.length < 2) return aria.trim();
    // Allow aria-label tooltips on icon-only buttons even with text
    // content, if the aria-label is longer / more descriptive.
    if (aria.trim().length > visibleText.length + 4) return aria.trim();
  }
  return '';
}

function findLabelledAncestor(el) {
  let node = el;
  let depth = 0;
  while (node && depth < 4) {
    if (node.getAttribute && getTooltipText(node)) return node;
    node = node.parentElement;
    depth++;
  }
  return null;
}

function positionTooltip(target) {
  if (!tooltipEl || !target) return;
  const rect = target.getBoundingClientRect();
  // Measure the tooltip after making it temporarily visible
  tooltipEl.style.visibility = 'hidden';
  tooltipEl.style.opacity = '1';
  const tw = tooltipEl.offsetWidth;
  const th = tooltipEl.offsetHeight;
  tooltipEl.style.visibility = '';
  let left = rect.left + rect.width / 2 - tw / 2;
  let top = rect.top - th - 8;
  // Flip below if there's no room above
  if (top < 8) top = rect.bottom + 8;
  // Clamp horizontally
  const maxLeft = window.innerWidth - tw - 8;
  if (left < 8) left = 8;
  if (left > maxLeft) left = maxLeft;
  tooltipEl.style.left = left + 'px';
  tooltipEl.style.top = top + 'px';
}

function show(target) {
  if (!tooltipEl) return;
  const text = getTooltipText(target);
  if (!text) return;
  currentTarget = target;
  tooltipEl.textContent = text;
  positionTooltip(target);
  tooltipEl.style.opacity = '1';
  // Strip the native title so the browser's own tooltip doesn't
  // double-up on top of ours. We restore it on mouseout.
  if (target.hasAttribute('title')) {
    originalTitles.set(target, target.getAttribute('title'));
    target.removeAttribute('title');
  }
}

function hide() {
  if (!tooltipEl) return;
  tooltipEl.style.opacity = '0';
  // Restore the native title attr
  if (currentTarget) {
    const stored = originalTitles.get(currentTarget);
    if (stored) {
      currentTarget.setAttribute('title', stored);
      originalTitles.delete(currentTarget);
    }
  }
  currentTarget = null;
}

function onPointerOver(e) {
  const target = findLabelledAncestor(e.target);
  if (!target || target === currentTarget) return;
  if (showTimer) clearTimeout(showTimer);
  // Small delay so moving the mouse across the page doesn't flash
  // tooltips everywhere the pointer grazes.
  showTimer = setTimeout(() => show(target), 250);
}

function onPointerOut(e) {
  if (showTimer) {
    clearTimeout(showTimer);
    showTimer = null;
  }
  if (!currentTarget) return;
  // Keep the tooltip up if moving between the target and a child
  const related = e.relatedTarget;
  if (related && currentTarget.contains(related)) return;
  hide();
}

function onFocusIn(e) {
  const target = findLabelledAncestor(e.target);
  if (!target) return;
  show(target);
}

function onFocusOut() {
  hide();
}

function onScrollOrResize() {
  // Reposition on scroll to keep the tooltip anchored to its target
  if (currentTarget) positionTooltip(currentTarget);
}

export function startAccessibleTooltips() {
  if (started) return;
  ensureTooltip();
  document.addEventListener('pointerover', onPointerOver, true);
  document.addEventListener('pointerout', onPointerOut, true);
  document.addEventListener('focusin', onFocusIn, true);
  document.addEventListener('focusout', onFocusOut, true);
  window.addEventListener('scroll', onScrollOrResize, true);
  window.addEventListener('resize', onScrollOrResize);
  started = true;
}

export function stopAccessibleTooltips() {
  if (!started) return;
  document.removeEventListener('pointerover', onPointerOver, true);
  document.removeEventListener('pointerout', onPointerOut, true);
  document.removeEventListener('focusin', onFocusIn, true);
  document.removeEventListener('focusout', onFocusOut, true);
  window.removeEventListener('scroll', onScrollOrResize, true);
  window.removeEventListener('resize', onScrollOrResize);
  // Restore any stripped titles
  if (currentTarget) {
    const stored = originalTitles.get(currentTarget);
    if (stored) currentTarget.setAttribute('title', stored);
  }
  if (tooltipEl && tooltipEl.parentNode)
    tooltipEl.parentNode.removeChild(tooltipEl);
  tooltipEl = null;
  currentTarget = null;
  if (showTimer) {
    clearTimeout(showTimer);
    showTimer = null;
  }
  started = false;
}
